import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserProvider, formatEther, getAddress, isAddress } from 'ethers';
import { WalletContext } from './WalletContext';
import { getLBTBalance, getStakeholderInfo } from '../services/blockchainClient';
import { refreshContractAddresses } from '../config/contracts';

// MetaMask requires unpadded hex (e.g. "0x7a69"), NOT zero-padded bytes (e.g. "0x007a69")
const toChainHex = (chainId) => '0x' + Number(chainId).toString(16);

const EXPECTED_CHAIN_ID = Number(process.env.REACT_APP_CHAIN_ID || 31337);
const currentLocation =
  typeof window !== 'undefined' && window.location
    ? window.location
    : null;

const currentHost = currentLocation?.hostname || 'localhost';
const currentProtocol = currentLocation?.protocol || 'http:';
const currentOrigin = currentLocation?.origin || '';
const isLocalHost = currentHost === 'localhost' || currentHost === '127.0.0.1';
const isSecureOrigin = currentProtocol === 'https:';

const parseRpcUrls = (value) =>
  String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

const normalizeRpcUrl = (value) => {
  const candidate = String(value || '').trim();
  if (!candidate) return '';

  if (candidate.startsWith('/')) {
    return currentOrigin ? `${currentOrigin}${candidate}` : '';
  }

  if (/^https?:\/\//i.test(candidate)) {
    return candidate;
  }

  return '';
};

const isAllowedRpcUrl = (rpcUrl) => {
  if (!rpcUrl) return false;
  if (!isSecureOrigin) return true;

  return (
    rpcUrl.startsWith('https://') ||
    rpcUrl.startsWith('http://localhost') ||
    rpcUrl.startsWith('http://127.0.0.1')
  );
};

const defaultRpcUrlsForChain = (chainId) => {
  switch (Number(chainId)) {
    case 1:
      return ['https://cloudflare-eth.com'];
    case 11155111:
      return ['https://rpc.sepolia.org'];
    case 137:
      return ['https://polygon-rpc.com'];
    case 31337:
      if (isLocalHost) {
        return ['http://localhost:8545'];
      }
      if (currentOrigin) {
        return [`${currentOrigin}/rpc`];
      }
      return [];
    case 1337:
      if (isLocalHost) {
        return ['http://localhost:8545'];
      }
      if (currentOrigin) {
        return [`${currentOrigin}/rpc`];
      }
      return [];
    default:
      return [];
  }
};

const CHAIN_RPC_URLS = (() => {
  const configuredRpcUrls = [
    ...parseRpcUrls(process.env.REACT_APP_CHAIN_RPC_URLS),
    ...parseRpcUrls(process.env.REACT_APP_BLOCKCHAIN_RPC_URL),
  ]
    .map(normalizeRpcUrl)
    .filter(isAllowedRpcUrl);

  const fallbackUrls = defaultRpcUrlsForChain(EXPECTED_CHAIN_ID)
    .map(normalizeRpcUrl)
    .filter(isAllowedRpcUrl);

  const unique = [];
  const seen = new Set();
  for (const url of [...configuredRpcUrls, ...fallbackUrls]) {
    if (url && !seen.has(url)) {
      seen.add(url);
      unique.push(url);
    }
  }
  return unique;
})();

const CHAIN_NAME_BY_ID = {
  1: 'Ethereum Mainnet',
  137: 'Polygon Mainnet',
  1337: 'LiveBetter Besu PoA',
  31337: 'Hardhat Local',
  11155111: 'Sepolia Testnet',
};

const CHAIN_NAME = CHAIN_NAME_BY_ID[EXPECTED_CHAIN_ID] || `LiveBetter Chain ${EXPECTED_CHAIN_ID}`;

const resolveNetworkName = (chainId, providerNetworkName) => {
  const numericChainId = Number(chainId);
  if (CHAIN_NAME_BY_ID[numericChainId]) {
    return CHAIN_NAME_BY_ID[numericChainId];
  }

  const normalizedName = String(providerNetworkName || '').trim();
  if (normalizedName && normalizedName.toLowerCase() !== 'unknown') {
    return normalizedName;
  }

  return `Chain ${numericChainId}`;
};

const isPendingWalletRequest = (error) => {
  const code = Number(error?.code ?? error?.error?.code ?? error?.data?.originalError?.code);
  const message = String(error?.message || '').toLowerCase();
  // Only treat genuine pending-request errors as -32002, NOT the separate
  // "RPC endpoint returned too many errors" variant which has the same code
  // but means the RPC URL itself is unreachable/unhealthy.
  return (code === -32002 && message.includes('request already pending')) ||
    message.includes('request already pending');
};

const isRpcEndpointError = (error) => {
  const code = Number(error?.code ?? error?.error?.code ?? error?.data?.originalError?.code);
  const message = String(error?.message || '').toLowerCase();
  return code === -32002 && message.includes('rpc endpoint');
};

const truncateAddress = (address) => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const ACCOUNT_SWITCH_TIMEOUT_MS = 30000;

const normalizeAddress = (value) => String(value || '').trim().toLowerCase();

const getDefaultSwitchState = (status = 'disconnected', activeAddress = '') => ({
  status,
  message: '',
  previousAddress: '',
  activeAddress,
  errorCode: null,
  updatedAt: Date.now(),
});

const isWalletLockedError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('locked');
};

const isUserRejectedError = (error) => {
  const code = Number(error?.code ?? error?.error?.code ?? error?.data?.originalError?.code);
  return code === 4001;
};

const withTimeout = async (promise, timeoutMs) => {
  let timeoutId;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          const timeoutError = new Error('Wallet request timed out');
          timeoutError.code = 'SWITCH_TIMEOUT';
          reject(timeoutError);
        }, timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
};

export const WalletProvider = ({ children }) => {
  const isSwitchingNetworkRef = useRef(false);
  const addressRef = useRef('');
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [address, setAddress] = useState('');
  const [chainId, setChainId] = useState(null);
  const [networkName, setNetworkName] = useState('');
  const [ethBalance, setEthBalance] = useState('0');
  const [lbtBalance, setLbtBalance] = useState('0');
  const [stakeholderStatus, setStakeholderStatus] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [accountSwitchState, setAccountSwitchState] = useState(() => getDefaultSwitchState());

  const refreshWalletState = useCallback(async () => {
    if (!window.ethereum) return;
    const browserProvider = new BrowserProvider(window.ethereum);
    const accounts = await browserProvider.listAccounts();
    if (!accounts || accounts.length === 0) {
      addressRef.current = '';
      setSigner(null);
      setAddress('');
      setProvider(browserProvider);
      setChainId(null);
      setNetworkName('');
      setEthBalance('0');
      setLbtBalance('0');
      setStakeholderStatus(null);
      setAccountSwitchState((prev) => ({
        ...prev,
        ...getDefaultSwitchState('disconnected'),
        message: prev.status === 'switch_failed' ? prev.message : '',
      }));
      return { address: '' };
    }

    const connectedSigner = await browserProvider.getSigner();
    const connectedAddress = await connectedSigner.getAddress();
    const checksumAddress = getAddress(connectedAddress);
    const network = await browserProvider.getNetwork();
    const balance = await browserProvider.getBalance(checksumAddress);

    setProvider(browserProvider);
    setSigner(connectedSigner);
    setAddress(checksumAddress);
    addressRef.current = checksumAddress;
    setChainId(Number(network.chainId));
    setNetworkName(resolveNetworkName(network.chainId, network.name));
    setEthBalance(formatEther(balance));
    localStorage.setItem('walletAddress', checksumAddress);

    // Fetch LBT balance (silently ignore if contract not deployed)
    try {
      const lbt = await getLBTBalance(browserProvider, checksumAddress);
      setLbtBalance(lbt);
    } catch {
      setLbtBalance('0');
    }

    // Fetch stakeholder registry status (silently ignore if contract not deployed)
    try {
      const stakeholder = await getStakeholderInfo(browserProvider, checksumAddress);
      setStakeholderStatus(stakeholder);
    } catch {
      setStakeholderStatus(null);
    }

    setAccountSwitchState((prev) => {
      const previousNormalized = normalizeAddress(prev.previousAddress);
      const activeNormalized = normalizeAddress(checksumAddress);
      const hasAddressChanged = Boolean(previousNormalized) && previousNormalized !== activeNormalized;

      if ((prev.status === 'wallet_prompt_open' || prev.status === 'switching_requested' || prev.status === 'manual_action_required') && hasAddressChanged) {
        return {
          status: 'switch_confirmed',
          message: 'Active account updated from MetaMask.',
          previousAddress: prev.previousAddress,
          activeAddress: checksumAddress,
          errorCode: null,
          updatedAt: Date.now(),
        };
      }

      if (prev.status === 'disconnected') {
        return {
          ...prev,
          ...getDefaultSwitchState('connected', checksumAddress),
        };
      }

      return {
        ...prev,
        activeAddress: checksumAddress,
        updatedAt: prev.updatedAt || Date.now(),
      };
    });

    return {
      address: checksumAddress,
      chainId: Number(network.chainId),
      networkName: resolveNetworkName(network.chainId, network.name),
    };
  }, []);

  const switchToExpectedNetwork = useCallback(async () => {
    if (!window.ethereum) {
      throw new Error('MetaMask provider not found');
    }
    if (isSwitchingNetworkRef.current) {
      return;
    }

    isSwitchingNetworkRef.current = true;
    const chainHex = toChainHex(EXPECTED_CHAIN_ID);
    try {
      // Always call wallet_addEthereumChain first so MetaMask is given the
      // correct RPC URL even when it already has chain 31337 stored with a
      // stale localhost:8545 entry. MetaMask will prompt the user to update
      // the network config if the chain already exists.
      if (CHAIN_RPC_URLS.length === 0) {
        // No custom RPC configured — plain switch is the only option.
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: chainHex }],
        });
      } else {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: chainHex,
                chainName: CHAIN_NAME,
                nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
                rpcUrls: CHAIN_RPC_URLS,
              },
            ],
          });
        } catch (addError) {
          if (isPendingWalletRequest(addError)) {
            throw new Error('MetaMask already has a pending network request. Open MetaMask and complete it, then retry.');
          }
          if (isRpcEndpointError(addError)) {
            throw new Error(
              'MetaMask cannot reach the blockchain RPC endpoint. ' +
              'Open MetaMask → Settings → Networks, find the “' + CHAIN_NAME + '” network ' +
              'and make sure its RPC URL is set to the correct HTTPS address, then retry.'
            );
          }
          // User rejected the add/update prompt — fall back to a plain switch
          // using whatever RPC MetaMask already has stored.
          if (addError.code === 4001) {
            await window.ethereum.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: chainHex }],
            });
          } else {
            throw addError;
          }
        }
      }
    } catch (error) {
      if (isPendingWalletRequest(error)) {
        throw new Error('MetaMask already has a pending network request. Open MetaMask and complete it, then retry.');
      }
      if (isRpcEndpointError(error)) {
        throw new Error(
          'MetaMask cannot reach the blockchain RPC endpoint. ' +
          'Open MetaMask → Settings → Networks, find the “' + CHAIN_NAME + '” network ' +
          'and make sure its RPC URL is set to the correct HTTPS address, then retry.'
        );
      }
      throw error;
    } finally {
      isSwitchingNetworkRef.current = false;
    }
    await refreshWalletState();
  }, [refreshWalletState]);

  const connectWallet = useCallback(async () => {
    if (!window.ethereum) {
      throw new Error('MetaMask is required to connect wallet');
    }

    setIsConnecting(true);
    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });
      const selectedAddress = accounts?.[0];
      if (!selectedAddress || !isAddress(selectedAddress)) {
        throw new Error('Wallet did not return a valid account');
      }
      await refreshWalletState();
      return selectedAddress;
    } finally {
      setIsConnecting(false);
    }
  }, [refreshWalletState]);

  // Force MetaMask to show the account picker so the user can switch accounts
  const switchAccount = useCallback(async () => {
    if (!window.ethereum) {
      throw new Error('MetaMask is required to switch accounts');
    }
    const previousAddress = addressRef.current;
    setIsSwitchingAccount(true);
    setAccountSwitchState({
      status: 'switching_requested',
      message: 'Request sent to MetaMask. Confirm the account to continue.',
      previousAddress,
      activeAddress: previousAddress,
      errorCode: null,
      updatedAt: Date.now(),
    });

    try {
      setAccountSwitchState((prev) => ({
        ...prev,
        status: 'wallet_prompt_open',
        message: 'MetaMask is open. Select an account there to switch in LiveBetter Hub.',
      }));

      await withTimeout(
        window.ethereum.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }],
        }),
        ACCOUNT_SWITCH_TIMEOUT_MS
      );

      const refreshResult = await refreshWalletState();
      const activeAddress = refreshResult?.address || addressRef.current || '';
      const hasAddressChanged =
        Boolean(normalizeAddress(previousAddress)) &&
        normalizeAddress(previousAddress) !== normalizeAddress(activeAddress);

      if (!activeAddress) {
        const nextState = {
          status: 'switch_failed',
          message: 'No active account found. Reconnect your wallet to continue.',
          previousAddress,
          activeAddress: '',
          errorCode: 'NO_ACTIVE_ACCOUNT',
          updatedAt: Date.now(),
        };
        setAccountSwitchState(nextState);
        return nextState;
      }

      if (hasAddressChanged) {
        const nextState = {
          status: 'switch_confirmed',
          message: 'Account switched successfully after wallet confirmation.',
          previousAddress,
          activeAddress,
          errorCode: null,
          updatedAt: Date.now(),
        };
        setAccountSwitchState(nextState);
        return nextState;
      }

      const nextState = {
        status: 'account_unchanged',
        message: 'The active account did not change. Choose another account in MetaMask to continue.',
        previousAddress,
        activeAddress,
        errorCode: 'ACCOUNT_UNCHANGED',
        updatedAt: Date.now(),
      };
      setAccountSwitchState(nextState);
      return nextState;
    } catch (error) {
      let nextState;
      if (error?.code === 'SWITCH_TIMEOUT' || isPendingWalletRequest(error)) {
        nextState = {
          status: 'manual_action_required',
          message: 'MetaMask still needs your input. Open MetaMask and switch account manually, then return here.',
          previousAddress,
          activeAddress: addressRef.current,
          errorCode: error?.code ?? -32002,
          updatedAt: Date.now(),
        };
      } else if (isUserRejectedError(error)) {
        nextState = {
          status: 'switch_failed',
          message: 'Account switch was canceled in MetaMask.',
          previousAddress,
          activeAddress: addressRef.current,
          errorCode: 4001,
          updatedAt: Date.now(),
        };
      } else if (isWalletLockedError(error)) {
        nextState = {
          status: 'manual_action_required',
          message: 'MetaMask is locked. Unlock it and switch account to continue.',
          previousAddress,
          activeAddress: addressRef.current,
          errorCode: 'WALLET_LOCKED',
          updatedAt: Date.now(),
        };
      } else {
        nextState = {
          status: 'switch_failed',
          message: error?.message || 'Unable to switch account.',
          previousAddress,
          activeAddress: addressRef.current,
          errorCode: error?.code ?? 'SWITCH_ERROR',
          updatedAt: Date.now(),
        };
      }

      setAccountSwitchState(nextState);
      return nextState;
    } finally {
      setIsSwitchingAccount(false);
    }
  }, [refreshWalletState]);

  // Clear local wallet state (MetaMask permission remains, but UI resets to disconnected)
  const disconnectWallet = useCallback(() => {
    addressRef.current = '';
    setSigner(null);
    setAddress('');
    setChainId(null);
    setNetworkName('');
    setEthBalance('0');
    setLbtBalance('0');
    setStakeholderStatus(null);
    setIsSwitchingAccount(false);
    setAccountSwitchState(getDefaultSwitchState('disconnected'));
    localStorage.removeItem('walletAddress');
  }, []);

  const clearAccountSwitchState = useCallback(() => {
    setAccountSwitchState((prev) => {
      if (!addressRef.current) {
        return getDefaultSwitchState('disconnected');
      }
      return {
        ...getDefaultSwitchState('connected', addressRef.current),
        message: '',
      };
    });
  }, []);

  // Fetch fresh contract addresses from the backend on mount.
  // This corrects stale addresses baked into the Docker image when the
  // frontend was built before the contract-deployer finished.
  useEffect(() => {
    refreshContractAddresses();
  }, []);

  useEffect(() => {
    refreshWalletState();
  }, [refreshWalletState]);

  useEffect(() => {
    if (!window.ethereum) return undefined;

    const handleAccountsChanged = async () => {
      await refreshWalletState();
    };
    const handleChainChanged = async () => {
      await refreshWalletState();
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [refreshWalletState]);

  const value = useMemo(
    () => ({
      address,
      addressDisplay: truncateAddress(address),
      networkName,
      chainId,
      ethBalance,
      lbtBalance,
      stakeholderStatus,
      isConnected: Boolean(address),
      isConnecting,
      isSwitchingAccount,
      accountSwitchState,
      isNetworkMismatch: chainId !== null && chainId !== EXPECTED_CHAIN_ID,
      provider,
      signer,
      connectWallet,
      switchAccount,
      disconnectWallet,
      switchToExpectedNetwork,
      refreshWalletState,
      clearAccountSwitchState,
    }),
    [
      address,
      networkName,
      chainId,
      ethBalance,
      lbtBalance,
      stakeholderStatus,
      isConnecting,
      isSwitchingAccount,
      accountSwitchState,
      provider,
      signer,
      connectWallet,
      switchAccount,
      disconnectWallet,
      switchToExpectedNetwork,
      refreshWalletState,
      clearAccountSwitchState,
    ]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};
